import dayjs from 'dayjs'
import { SHIFT, VISIT } from '../constants'
import { RateType } from '../hooks/usePaymentCards'
import type { TeamMemberRateDto } from '../hooks/useRevisionUpdate'
import type { RotaEventInstance } from '../types'
import {
  getDateTimeInZone,
  TIME_FORMAT,
  YEAR_FIRST_DATE_FORMAT
} from '../utils.datetime'

// `changes` isn't a RotaEventInstance field; kept for a legacy revision-diff shape
// this function has always defensively supported alongside the plain `teamMembers` field.
type RevisionValues = Partial<RotaEventInstance> & {
  changes?: { teamMembers?: unknown }
}

type RevisionTeamMember = {
  teamMemberId: string
  carer?: { name?: { first?: string; last?: string } }
  payRateOverride?: {
    rate?: string | number
    rateCardId?: string
    customRate?: number
    payRateType?: string
    rateType?: string
    type?: string
  } | null
  rate?: string | number
  rateType?: string
}

export type TeamMemberPayRateOverrideForm = {
  overridePayRate: boolean
  customRate?: number
  rateType?: RateType
}

export function getTeamMemberFormId(entry: unknown): string {
  return typeof entry === 'object' && entry != null && 'value' in entry
    ? (entry as { value: string }).value
    : String(entry)
}

export function normalizeTeamMemberFormIds(teamMembers: unknown): string[] {
  if (!Array.isArray(teamMembers)) {
    return []
  }
  return teamMembers.map(getTeamMemberFormId)
}

export function getTeamMemberDisplayName(
  teamMemberId: string,
  formTeamMembers: unknown,
  revisionTeamMembers: RevisionTeamMember[] | undefined
): string {
  if (Array.isArray(formTeamMembers)) {
    const fromForm = formTeamMembers.find((m: unknown) =>
      typeof m === 'object' && m != null && 'value' in m
        ? (m as { value: unknown }).value === teamMemberId
        : m === teamMemberId
    )
    if (
      fromForm &&
      typeof fromForm === 'object' &&
      'label' in fromForm &&
      fromForm.label
    ) {
      return String(fromForm.label)
    }
  }
  const fromRev = revisionTeamMembers?.find(
    (m) => m.teamMemberId === teamMemberId
  )
  const n = fromRev?.carer?.name
  if (n?.first || n?.last) {
    return `${n.first ?? ''} ${n.last ?? ''}`.trim()
  }
  return teamMemberId
}

export function getRevisionTeamMembersSource(
  values: RevisionValues
): RevisionTeamMember[] | undefined {
  const changes = values?.changes
  const changesTeamMembers =
    typeof changes === 'object' && changes != null && 'teamMembers' in changes
      ? (changes as Record<string, unknown>).teamMembers
      : undefined
  const list = values?.teamMembers ?? changesTeamMembers
  return Array.isArray(list) ? (list as RevisionTeamMember[]) : undefined
}

function parseMemberRateType(
  raw: unknown,
  nested: Record<string, unknown> | null | undefined
): RateType {
  const fromNested = nested?.payRateType ?? nested?.rateType ?? nested?.type
  const typeRaw = fromNested ?? raw
  if (
    typeRaw === RateType.EVENT ||
    typeRaw === 'EVENT' ||
    typeRaw === 'FIXED'
  ) {
    return RateType.EVENT
  }
  return RateType.HOURLY
}

export function buildTeamMemberPayRateOverridesInitial(
  teamMembers: RevisionTeamMember[] | undefined
): Record<string, TeamMemberPayRateOverrideForm> {
  const record: Record<string, TeamMemberPayRateOverrideForm> = {}
  for (const m of teamMembers ?? []) {
    const id = m?.teamMemberId
    if (!id) {
      continue
    }
    const po = m?.payRateOverride
    const rateStr =
      po != null && typeof po === 'object' && po.rate != null && po.rate !== ''
        ? String(po.rate)
        : m?.rate != null && m?.rate !== ''
          ? String(m.rate)
          : undefined
    const hasRate = rateStr != null && !Number.isNaN(Number(rateStr))
    const overridePayRate = Boolean(po) || hasRate
    const rateType = parseMemberRateType(m?.rateType, po)

    record[id] = {
      overridePayRate,
      customRate: hasRate ? Number(rateStr) : undefined,
      rateType
    }
  }
  return record
}

export function buildTeamMemberRatesPayload(
  overrides: Record<string, TeamMemberPayRateOverrideForm> | undefined,
  assignedTeamMemberIds: string[]
): TeamMemberRateDto[] {
  return assignedTeamMemberIds.map((teamMemberId) => {
    const o = overrides?.[teamMemberId]
    if (
      !o?.overridePayRate ||
      o.customRate == null ||
      Number.isNaN(Number(o.customRate))
    ) {
      return { teamMemberId, rateType: undefined }
    }
    const n = Number(o.customRate)
    return {
      teamMemberId,
      rate: n.toFixed(2),
      rateType: o.rateType
    }
  })
}

export const getRevisionFormInitialValues = (values: RevisionValues) => {
  const overrideRaw = values?.invoicingRateOverride
  const override =
    typeof overrideRaw === 'object' && overrideRaw != null
      ? (overrideRaw as { rateCardId?: string; customRate?: number })
      : undefined
  return {
    ...values,
    slotNotes: values?.description,
    // utc() is called here to normalize antd date/time picker to understand correct behaviour, to avoid current user zone effect
    patternStart: getDateTimeInZone(values?.startTime).utc(),
    type: values?.siteShift === true ? SHIFT : VISIT,
    startTime: dayjs(values?.startTime).utc().format(TIME_FORMAT),
    endTime: dayjs(values?.endTime).utc().format(TIME_FORMAT),
    // Invoicing rate override fields
    overrideInvoicingRate: !!override,
    invoicingRateCardId:
      override?.rateCardId ??
      (override?.customRate != null ? '__custom__' : undefined),
    customInvoicingRate: override?.customRate ?? undefined
  }
}

export const getRevisionSubmitValues = (
  values: Record<string, unknown>,
  isTimeTouched?: boolean | undefined,
  isSiteShift?: boolean | undefined
) => {
  // Build invoicingRateOverride from the override form fields
  let invoicingRateOverride:
    | { rateCardId?: string; customRate?: number; unit?: string }
    | null
    | undefined
  if ('overrideInvoicingRate' in values) {
    if (!values.overrideInvoicingRate) {
      invoicingRateOverride = null // explicitly remove override
    } else if (values.invoicingRateCardId === '__custom__') {
      invoicingRateOverride = {
        customRate: values.customInvoicingRate as number | undefined,
        unit: 'HOUR'
      }
    } else if (values.invoicingRateCardId) {
      invoicingRateOverride = {
        rateCardId: values.invoicingRateCardId as string
      }
    }
    // defensive: override toggled on but no rate card resolved — should be caught by form
    // validation, but treat as no override rather than silently sending undefined
    // (invoicingRateOverride remains undefined → field omitted from payload → no change)
  }

  const transformedValues = {
    title: values?.title,
    date:
      values?.patternStart &&
      dayjs(values?.patternStart as string).format(YEAR_FIRST_DATE_FORMAT),
    ...(isTimeTouched && values?.startTime
      ? { startTime: values.startTime as string }
      : {}),
    ...(isTimeTouched && values?.endTime
      ? { endTime: values.endTime as string }
      : {}),
    flexiType: values?.flexiType,
    plannedDuration: values?.plannedDuration,
    addressId: isSiteShift ? undefined : values?.addressId,
    fundingTypeId: values?.fundingTypeId,
    description: values?.slotNotes,
    instanceTypeId: values?.instanceTypeId,
    ...(invoicingRateOverride !== undefined && { invoicingRateOverride })
  }

  return transformedValues
}

export const getRevisionReqFormInitialValues = (values: RevisionValues) => {
  const teamMembersSource = getRevisionTeamMembersSource(values)
  const mappedTeamMembers = teamMembersSource?.map((member) => ({
    label:
      member.carer?.name?.first && member.carer?.name?.last
        ? `${member.carer.name.first} ${member.carer.name.last}`
        : undefined,
    value: member.teamMemberId || undefined
  }))
  return {
    ...values,
    slotNotes: values?.description,
    type: values?.siteShift === true ? 'shift' : 'visit',
    teamMembers: mappedTeamMembers,
    teamMemberPayRateOverrides:
      buildTeamMemberPayRateOverridesInitial(teamMembersSource)
  }
}
